import type { TranslationKey } from '../i18n'

export type NumericRule = {
  labelKey: TranslationKey
  min?: number
  max?: number
  allowDecimal?: boolean
  requireInteger?: boolean
}

export type ParsedNumberResult =
  | { ok: true; value: number }
  | { ok: false }

const stripSpaces = (value: string): string =>
  value.replace(/[\s\u00A0\u202F]/g, '')

const isDotGroupedInteger = (value: string): boolean =>
  /^-?[1-9]\d{0,2}(\.\d{3})+$/.test(value)

const isCommaGroupedInteger = (value: string): boolean =>
  /^-?[1-9]\d{0,2}(,\d{3})+$/.test(value)

const isDotGroupedWithCommaDecimal = (value: string): boolean =>
  /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(value)

const isCommaGroupedWithDotDecimal = (value: string): boolean =>
  /^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(value)

export const parseHumanNumber = (raw: string, rule: Pick<NumericRule, 'allowDecimal' | 'requireInteger'>): ParsedNumberResult => {
  const trimmed = (raw ?? '').toString().trim()
  if (!trimmed) return { ok: false }

  let value = stripSpaces(trimmed)

  // Keep only digits, separators, and leading minus.
  // (e.g. users sometimes paste "2.500 peces" or similar.)
  value = value.replace(/(?!^)-/g, '')
  value = value.replace(/[^0-9,.-]/g, '')

  if (!value || value === '-' || value === '.' || value === ',') return { ok: false }

  const hasDot = value.includes('.')
  const hasComma = value.includes(',')

  if (hasDot && hasComma) {
    // Prefer known grouping+decimal patterns.
    if (isDotGroupedWithCommaDecimal(value)) {
      // 1.234,56 -> 1234.56
      value = value.replace(/\./g, '').replace(/,/g, '.')
    } else if (isCommaGroupedWithDotDecimal(value)) {
      // 1,234.56 -> 1234.56
      value = value.replace(/,/g, '')
    } else {
      // Fallback: treat comma as thousands (existing behavior) unless decimals are expected.
      if (rule.allowDecimal && !rule.requireInteger) {
        // Assume last separator is decimal.
        const lastComma = value.lastIndexOf(',')
        const lastDot = value.lastIndexOf('.')
        if (lastComma > lastDot) {
          value = value.replace(/\./g, '').replace(/,/g, '.')
        } else {
          value = value.replace(/,/g, '')
        }
      } else {
        value = value.replace(/,/g, '')
      }
    }
  } else if (hasComma) {
    if (isCommaGroupedInteger(value)) {
      // 1,234,567 -> 1234567
      value = value.replace(/,/g, '')
    } else if (rule.allowDecimal && !rule.requireInteger) {
      // 3,5 -> 3.5
      value = value.replace(/,/g, '.')
    } else {
      // Integer fields: allow "2500,0" style when decimals are all zeros.
      const m = value.match(/^-?(\d+),(\d+)$/)
      if (m && /^0+$/.test(m[2] ?? '')) {
        value = `${value.startsWith('-') ? '-' : ''}${m[1]}`
      }
      // Otherwise, keep comma as-is; Number() will fail and surface a validation error.
    }
  } else if (hasDot) {
    // If it's clearly thousand-grouped, treat as grouping even for decimal fields.
    if (isDotGroupedInteger(value)) {
      value = value.replace(/\./g, '')
    }
  }

  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return { ok: false }
  return { ok: true, value: numeric }
}
